<template>
    <div>
        <ul v-show="enabled" class="no-bullet tab-list" role="tablist" @keydown="changeFocus">
            <li v-for="(tab, i) in tabs" :key="`button-${i}`" :class="{ 'is-active': isActive(i) }" role="presentation">
                <component
                    :is="headingLevel"
                    :id="id"
                    ref="tabTriggers"
                    role="tab"
                    class="tab-button"
                    :class="{ 'is-active': isActive(i) }"
                    :tabindex="isActive(i) ? 0 : -1"
                    :aria-selected="isActive(i).toString()"
                    @click="setActiveTab(i)"
                    @keyup.enter="setActiveTab(i)"
                >
                    <component v-if="tab.icon" class="tab-icon" :is="tab.icon" width="14" />
                    {{ tab.name }}
                    <span v-if="tab.subtitle" class="tab-subtitle">{{ tab.subtitle }}</span>
                </component>
            </li>
        </ul>
        <section v-for="(tab, i) in tabs" :key="`panel-${i}`" role="tabpanel" v-show="activeTab === i || !enabled">
            <slot :name="`tab${i}`" />
        </section>
    </div>
</template>

<script setup>
import { ref, useId } from 'vue';

const props = defineProps({
    tabs: { type: Array, required: true },
    enabled: { type: Boolean, default: true },
    headingLevel: { type: String, default: 'h2' },
});

const tabTriggers = ref([]);
const activeTab = ref(0);
const id = useId();

function setActiveTab(index) {
    activeTab.value = index;
}

function isActive(index) {
    return activeTab.value === index;
}

function changeFocus(event) {
    const key = event.keyCode;
    const ctrlModifier = event.ctrlKey && [33, 34].includes(key);
    const length = tabTriggers.value.length;
    const target = event.target;

    if ([37, 38, 39, 40].includes(key) || ctrlModifier) {
        const index = tabTriggers.value.indexOf(target);
        const direction = [39, 40, 34].includes(key) ? 1 : -1;
        const newIndex = (index + length + direction) % length;
        tabTriggers.value[newIndex]?.focus();
        event.preventDefault();
    } else if ([35, 36].includes(key)) {
        if (key === 36) tabTriggers.value[0]?.focus();
        else tabTriggers.value[length - 1]?.focus();
        event.preventDefault();
    }
}
</script>

<style lang="scss" scoped>
.tab-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    overflow-x: auto;
    width: 100%;
    margin: 0;
    flex-shrink: 0;
    background-color: $color-black-2;
    border-radius: 15px;
    padding: 4px;
    @include breakpoint(medium) {
        grid-template-columns: repeat(auto-fill, minmax(0, 160px));
    }
}
.tab-button {
    @include rem-typo(14px);
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
    border-radius: 12px;
    padding: 16px 24px;
    background-color: transparent;
    color: $color-blue-70;
    border: 0;
    transition: background-color $default-transition;
    margin: 0;
    cursor: pointer;
    height: 100%;
    @include breakpoint(large) {
        @include rem-typo(20px);
    }
    &:hover,
    &:focus-visible {
        background-color: $color-blue-80;
        color: $color-white;
    }
    &.is-active {
        z-index: 1;
        position: relative;
        background-color: $color-blue-80;
        color: $color-white;
    }
    .tab-icon {
        flex-shrink: 0;
    }
    .tab-subtitle {
        @include rem-typo(12px);
        display: block;
        font-weight: 400;
        @include breakpoint(large) {
            @include rem-typo(14px);
        }
    }
    .arret-search-widget & {
        @include rem-typo(16px);
        padding: 0 12px;
        @include breakpoint(large) {
            @include rem-typo(20px);
            padding: 16px;
        }
    }
}
</style>
